import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import 'hammerjs'
import ReceivableChart from './Charts/ReceivableChart'
import ReceivableTable from './Tables/ReceivableTable'
import api from 'api'

import ConnectButton from 'components/Common/Button/ConnectButton'
import ZendeskLink from 'components/Common/ZendeskLink'
import { Box, Link, Typography } from '@mui/material'
import { RecievableInvoiceTab } from './RecievableInvoiceTab'

import FinancialOverviewPlaceholder from './FinancialOverviewPlaceholder'

import { styled } from '@mui/material/styles'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

const SelectIcon = styled(Box)(({ theme }) => ({
  background: theme.palette.color.darkBlue5,
  borderRadius: '4px',
  width: '30px',
  height: '30px',
  marginLeft: '20px',
  '&:hover': {
    background: theme.palette.color.darkBlue10,
  },
}))

const StyledMenu = styled(props => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 5,
    minWidth: '12%',
    color: '#66668F',
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: '#9292AF',
        marginRight: theme.spacing(1.5),
        bottom: '10px',
      },
      '&:active': {
        backgroundColor: '#F4F4F7',
        borderRadius: '5px',
      },
      '&:hover': {
        backgroundColor: '#F4F4F7',
        borderRadius: '5px',
      },
    },
  },
}))

function ReceivableTab() {
  const [anchorEl, setAnchorEl] = useState(null)
  const [details, SetDetails] = useState(false)
  const [insights, setInsights] = useState(true)
  const [invoiceDetails, setInvoiceDetails] = useState(0)
  const [buttonName, setButtonName] = useState('Insights')
  const companyId = useSelector(state => state.apiResponseReducers.companyId)
  const daySpanSelected = useSelector(state => state.appReducer.financialOverviewDaySpan)
  const open = Boolean(anchorEl)
  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleInsights = () => {
    setInsights(true)
    SetDetails(false)
    setButtonName('Insights')
    handleClose()
  }

  const handleDetails = () => {
    SetDetails(true)
    setInsights(false)
    setButtonName('Details')
    handleClose()
  }

  const invoiceSummary = async () => {
    const res = await api.getInvoiceSummary(companyId, daySpanSelected)
    setInvoiceDetails(res)
  }

  useEffect(() => {
    invoiceSummary()
    // eslint-disable-next-line
  }, [daySpanSelected])

  const graphImage = process.env['REACT_APP_BRC_IMAGE_URL'] + '/svg/cashflow_popup_cashflow.svg'
  const tableImage =
    process.env['REACT_APP_BRC_IMAGE_URL'] + '/svg/cashlow_popup_cashflow_invoices_table.svg'
  const codatConnected = useSelector(state => state.appReducer.accountCodatConnected)
  return (
    <>
      <Box marginBottom="20px" display="flex" justifyContent="flex-end">
        <Button
          id="demo-customized-button"
          aria-controls={open ? 'demo-customized-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          disableElevation
          onClick={handleClick}
          endIcon={
            <Box component="span">
              <SelectIcon>
                <ArrowDropDownIcon sx={{ fontSize: '24px', color: '#9292AF', marginBottom: '5px' }} />
              </SelectIcon>
            </Box>
          }
          sx={{
            color: '#66668F',
            fontFamily: 'poppins',
            fontSize: '16px',
            border: '1px solid #D3D3DF',
            width: '150px',
            textTransform: 'capitalize',
            fontWeight: '400',
          }}
        >
          {buttonName}
        </Button>
        <StyledMenu
          id="demo-customized-menu"
          MenuListProps={{
            'aria-labelledby': 'demo-customized-button',
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          sx={{ borderRadius: '5px' }}
        >
          <MenuItem onClick={() => handleInsights()} disableRipple>
            Insights
          </MenuItem>
          <MenuItem onClick={() => handleDetails()} disableRipple>
            Details
          </MenuItem>
        </StyledMenu>
      </Box>

      {details && (
        <div className="d-flex flex-column h-100 k-relative">
          {codatConnected && <ReceivableChart />}
          {codatConnected && <ReceivableTable />}
          {!codatConnected && (
            <FinancialOverviewPlaceholder isWithDropPlaceholder graphImage={graphImage} tableImage={tableImage} />
          )}
          {!codatConnected && (
            <div className=" bg-transparent border-light align-text-bottom text-center mt-3 px-3">
              <ConnectButton label="Connect your accounting application" isConnected={codatConnected} />
              <p className="pb-3 disclaimer-text">
                When you connect your accounting application you will see your past transactions.
              </p>
            </div>
          )}
          <div className="dialog-markaaz-payment-container position-relative text-center">
            <div className="markaaz-payments-link-container">
              <p className="p-3 mb-0" style={{ color: '#25245f' }}>
                <span className="px-1">
                  <b>COMING SOON:</b>
                </span>
                pay and get paid through Markaaz.
                <span className="px-1">
                  <Link
                    rel="noreferrer noopener"
                    href="https://markaaz.com/blog/"
                    target="_blank"
                    underline="hover"
                    className="markaaz-payments-link"
                  >
                    <Typography component="span" variant="body2B" color="secondary">
                      Learn more about Payments coming soon!
                    </Typography>
                  </Link>
                </span>
              </p>
              <div className="link-hover-message">
                Paying and getting paid on Markaaz is different than anywhere else. Markaaz has negotiated the best
                rates on your behalf for every transaction. Learn more about payments coming soon!
              </div>
            </div>
          </div>

          <ZendeskLink />
        </div>
      )}
      {insights && (
        <RecievableInvoiceTab
          invoiceDetails={invoiceDetails}
          daySpanSelected={daySpanSelected}
          codatConnected={codatConnected}
        />
      )}
    </>
  )
}

export default ReceivableTab
